import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { romaneioService } from '../../../../../api';
import { useOps } from '../../../../../hooks/useOps';
import { formatCurrencyBrlFromCents, formatDateIso } from '../../../../../utils';
import { AntecipacaoData, AntecipacaoResponse } from '../types';

interface UseAntecipacaoResult {
  currentOps: ReturnType<typeof useOps>['currentOps'];
  data: AntecipacaoResponse | null;
  loading: boolean;
  isFetching: boolean;
  error: string;
  selectedDate: Date;
  columns: ColumnDef<AntecipacaoData>[];
  formatCurrency: (value: number) => string;
  handleDateChange: (date: Date | undefined) => void;
  loadData: () => Promise<void>;
}

const antecipacaoQueryKeys = {
  all: ['romaneio-antecipacao'] as const,
  byDate: (filterDate: string, ops: string, dbName: string) =>
    [...antecipacaoQueryKeys.all, filterDate, ops, dbName] as const,
};

async function fetchAntecipacao(params: {
  filterDate: string;
  dbName: string;
  ops: string;
}): Promise<AntecipacaoResponse | null> {
  try {
    const response = await romaneioService.getAntecipacaoViz(params);

    if (response.success) {
      return response.data ?? null;
    }

    throw new Error(response.message || 'Erro ao carregar dados de antecipação');
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Erro ao conectar com o servidor');
  }
}

export function useAntecipacao(): UseAntecipacaoResult {
  const { currentOps, getCurrentOpsConfig } = useOps();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const opsConfig = getCurrentOpsConfig();
  const dbName = opsConfig?.dbName;
  const filterDate = format(selectedDate, 'yyyy-MM-dd');
  const enabled = Boolean(currentOps && dbName);

  const columns: ColumnDef<AntecipacaoData>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 150,
      },
      {
        accessorKey: 'ifoodUuid',
        header: 'iFood UUID',
        size: 200,
        cell: ({ getValue }) => {
          const uuid = getValue() as string;
          if (!uuid) {
            return <span className="text-[hsl(215,16%,47%)]">N/A</span>;
          }
          return <span className="font-mono text-xs">{uuid}</span>;
        },
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome Completo',
        size: 180,
        cell: ({ getValue }) => {
          const nome = getValue() as string;
          if (!nome) {
            return <span className="text-[hsl(215,16%,47%)]">N/A</span>;
          }
          return <span>{nome}</span>;
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 130,
        cell: ({ getValue }) => {
          const telefone = getValue() as string;
          if (!telefone) {
            return <span className="text-[hsl(215,16%,47%)]">N/A</span>;
          }
          return <span>{telefone}</span>;
        },
      },
      {
        accessorKey: 'valorFace',
        header: 'Valor Face',
        size: 130,
        cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
      },
      {
        accessorKey: 'valorCompra',
        header: 'Valor Compra',
        size: 130,
        cell: ({ getValue }) => formatCurrencyBrlFromCents(getValue() as number),
      },
      {
        accessorKey: 'dataRomaneio',
        header: 'Data Romaneio',
        size: 130,
        cell: ({ getValue }) => formatDateIso(getValue() as string),
      },
      {
        accessorKey: 'dataVencimento',
        header: 'Data Vencimento',
        size: 140,
        cell: ({ getValue }) => formatDateIso(getValue() as string),
      },
      {
        accessorKey: 'comprovanteBancario',
        header: 'Comprovante Bancário',
        size: 180,
        cell: ({ getValue }) => {
          const url = getValue() as string;
          if (!url) {
            return <span className="text-[hsl(215,16%,47%)]">N/A</span>;
          }

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,25%)] underline inline-flex items-center gap-1"
            >
              Ver comprovante
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        },
      },
    ],
    []
  );

  const query = useQuery({
    queryKey: antecipacaoQueryKeys.byDate(filterDate, currentOps || '', dbName || ''),
    queryFn: () =>
      fetchAntecipacao({
        filterDate,
        dbName: dbName!,
        ops: currentOps!,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    placeholderData: (previousData) => previousData,
  });

  const isImile = query.data?.isImile === true;
  const visibleColumns = useMemo(
    () =>
      isImile
        ? columns.filter((c) => (c as { accessorKey?: string }).accessorKey !== 'ifoodUuid')
        : columns,
    [columns, isImile]
  );

  const loadData = useCallback(async () => {
    await query.refetch();
  }, [query]);

  const handleDateChange = useCallback((date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
    }
  }, []);

  return {
    currentOps,
    data: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error?.message || 'Erro ao conectar com o servidor' : '',
    selectedDate,
    columns: visibleColumns,
    formatCurrency: formatCurrencyBrlFromCents,
    handleDateChange,
    loadData,
  };
}
